/**
 * AuditYearSettings — settings page to create and manage audit years.
 * Route: /settings/audit-years
 *
 * An "Audit Year" is the top-level working context for the platform.
 * When you select a year in the sidebar, all pages show data for that year.
 * Locked years are historical read-only archives.
 */
import React, { useState } from 'react';
import { Plus, Trash2, Pencil, X, Check, Lock, DatabaseZap, AlertTriangle, CheckCircle2, XCircle, Power } from 'lucide-react';
import {
  useAuditYears,
  useCreateAuditYear,
  useUpdateAuditYear,
  useLockAuditYear,
  useDeleteAuditYear,
  useLockReadiness,
  useBackfillPreview,
  useBackfillAuditYear,
} from '../hooks/useAuditYears.ts';
import type { AuditYear, BackfillCounts } from '../api/auditYears.ts';
import { getApiErrorMessage, resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import toast from 'react-hot-toast';

// ─── Create form ─────────────────────────────────────────────────────────────

function CreateAuditYearForm({ onClose }: { onClose: () => void }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [name, setName] = useState(`FY ${currentYear}`);
  const { mutate, isPending } = useCreateAuditYear();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(
      { year, name },
      {
        onSuccess: () => { toast.success('Audit year created'); onClose(); },
        onError: (err) => {
          toast.error(
            resolve403ErrorMessage(
              err,
              (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                'Failed to create audit year'
            )
          );
        },
      }
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm"
    >
      <h3 className="text-base font-semibold text-gray-900">New Audit Year</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
          <input
            type="number"
            value={year}
            onChange={(e) => {
              const y = Number(e.target.value);
              setYear(y);
              setName(`FY ${y}`);
            }}
            min={2000}
            max={2100}
            required
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="px-4 py-2 text-sm bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-50"
        >
          {isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// ─── Backfill modal ───────────────────────────────────────────────────────────

function BackfillModal({ ay, onClose }: { ay: AuditYear; onClose: () => void }) {
  const { data: counts, isLoading, isError } = useBackfillPreview(ay.id);
  const { mutate: runBackfill, isPending, isSuccess, data: result } = useBackfillAuditYear();

  const handleConfirm = () => {
    runBackfill(ay.id, {
      onSuccess: () => toast.success('Records assigned successfully'),
      onError: (err) => toast.error(resolve403ErrorMessage(err, 'Backfill failed — check the console')),
    });
  };

  const labelFor: Record<keyof Omit<BackfillCounts, 'total'>, string> = {
    processes: 'Processes',
    sox_issues: 'Issues',
    test_runs: 'Test Runs',
    documents: 'Documents / Reports',
    test_executions: 'Test Executions',
    process_risk_controls: 'Risk–Control Mappings',
    pbc_requests: 'PBC Requests',
    materiality_assessments: 'Materiality Assessments',
    system_itgc_controls: 'System ITGC Controls',
  };

  const displayCounts: BackfillCounts | undefined = isSuccess ? result?.counts : counts;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <DatabaseZap className="h-4 w-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900">
              Assign unscoped records to <span className="text-blue-700">{ay.name}</span>
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          {isLoading && (
            <p className="text-sm text-gray-500">Counting unscoped records…</p>
          )}
          {isError && (
            <p className="text-sm text-red-600">Failed to fetch preview. Please try again.</p>
          )}
          {displayCounts && (
            <>
              {!isSuccess && (
                <p className="text-sm text-gray-600">
                  The following records have no audit year assigned. Clicking{' '}
                  <strong>Assign Records</strong> will stamp them all with{' '}
                  <strong>{ay.name}</strong>. This cannot be undone.
                </p>
              )}
              {isSuccess && (
                <p className="text-sm text-green-700 font-medium">
                  ✓ {result?.message}
                </p>
              )}
              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-2 text-left font-medium">Entity</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {isSuccess ? 'Assigned' : 'Unscoped'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(Object.keys(labelFor) as Array<keyof typeof labelFor>).map((key) => (
                      <tr key={key} className={displayCounts[key] === 0 ? 'opacity-40' : ''}>
                        <td className="px-3 py-1.5 text-gray-700">{labelFor[key]}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums text-gray-900">
                          {displayCounts[key]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-3 py-2 text-gray-800">Total</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-blue-700">
                        {displayCounts.total}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {isSuccess ? 'Close' : 'Cancel'}
          </button>
          {!isSuccess && (
            <button
              onClick={handleConfirm}
              disabled={isPending || isLoading || !counts || counts.total === 0}
              className="px-4 py-2 text-sm bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-50"
            >
              {isPending ? 'Assigning…' : `Assign ${counts?.total ?? '…'} Records`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Lock Readiness Modal ─────────────────────────────────────────────────────

function LockReadinessModal({ ay, onClose }: { ay: AuditYear; onClose: () => void }) {
  const { data: readiness, isLoading, isError } = useLockReadiness(ay.id);
  const { mutate: lockIt, isPending, isSuccess } = useLockAuditYear();

  const handleLock = () => {
    lockIt(ay.id, {
      onSuccess: () => toast.success(`${ay.name} is now locked — historical read-only`),
      onError: (err: any) => {
        const msg = err?.response?.data?.error ?? 'Lock failed';
        toast.error(msg);
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-gray-900">
              Lock <span className="text-amber-600">{ay.name}</span>
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          {isLoading && (
            <p className="text-sm text-gray-500 animate-pulse">Checking readiness…</p>
          )}
          {isError && (
            <p className="text-sm text-red-600">Failed to check readiness. Please try again.</p>
          )}
          {isSuccess && (
            <p className="text-sm text-green-700 font-medium flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              {ay.name} is now locked — all data is read-only.
            </p>
          )}
          {readiness && !isSuccess && (
            <>
              {readiness.blockers.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
                    Must resolve before locking
                  </p>
                  {readiness.blockers.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              )}
              {readiness.warnings.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                    Warnings (won't block lock)
                  </p>
                  {readiness.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
              {readiness.blockers.length === 0 && readiness.warnings.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  All checks passed — ready to lock.
                </div>
              )}
              {readiness.blockers.length === 0 && (
                <p className="text-sm text-gray-600">
                  Locking <strong>{ay.name}</strong> makes it a historical read-only archive.
                  No changes can be made to any data in this year after locking.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {isSuccess ? 'Close' : 'Cancel'}
          </button>
          {!isSuccess && (
            <button
              onClick={handleLock}
              disabled={isPending || isLoading || !readiness || !readiness.can_lock}
              className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Lock className="h-3.5 w-3.5" />
              {isPending ? 'Locking…' : 'Lock Year'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function AuditYearRow({ ay }: { ay: AuditYear }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(ay.name);
  const [showBackfill, setShowBackfill] = useState(false);
  const [showLockReadiness, setShowLockReadiness] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const [confirmingActivate, setConfirmingActivate] = useState(false);
  const { mutate: update, isPending: updating } = useUpdateAuditYear();
  const { mutate: del, isPending: deleting } = useDeleteAuditYear();
  // Note: locking is handled inside LockReadinessModal — no useLockAuditYear needed here.

  const handleSave = () => {
    update(
      { id: ay.id, payload: { name: editName } },
      {
        onSuccess: () => { toast.success('Updated'); setEditing(false); },
        onError: (err) => toast.error(resolve403ErrorMessage(err, 'Update failed')),
      }
    );
  };

  const handleDelete = () => {
    del(ay.id, {
      onSuccess: () => { toast.success('Deleted'); setConfirmingDelete(false); },
      onError: (err) => {
        setConfirmingDelete(false);
        toast.error(resolve403ErrorMessage(err, getApiErrorMessage(err, 'Delete failed')));
      },
    });
  };

  const handleDeactivate = () => {
    update(
      { id: ay.id, payload: { is_active: false } },
      {
        onSuccess: () => { toast.success(`${ay.name} deactivated`); setConfirmingDeactivate(false); },
        onError: (err) => {
          setConfirmingDeactivate(false);
          toast.error(resolve403ErrorMessage(err, getApiErrorMessage(err, 'Deactivate failed')));
        },
      }
    );
  };

  const handleActivate = () => {
    update(
      { id: ay.id, payload: { is_active: true } },
      {
        onSuccess: () => { toast.success(`${ay.name} activated`); setConfirmingActivate(false); },
        onError: (err) => {
          setConfirmingActivate(false);
          toast.error(resolve403ErrorMessage(err, getApiErrorMessage(err, 'Activate failed')));
        },
      }
    );
  };

  return (
    <>
      {showBackfill && <BackfillModal ay={ay} onClose={() => setShowBackfill(false)} />}
      {showLockReadiness && <LockReadinessModal ay={ay} onClose={() => setShowLockReadiness(false)} />}
      <div className="border border-gray-200 rounded-lg px-4 py-3 bg-white flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {editing ? (
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="border border-blue-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <span className="font-medium text-gray-900 text-sm">{ay.name}</span>
          )}
          <span className="text-xs text-gray-400">{ay.year}</span>
          {ay.is_locked && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
              <Lock className="h-3 w-3" /> Locked
            </span>
          )}
          {!ay.is_locked && ay.is_active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
              Active
            </span>
          )}
          {!ay.is_locked && !ay.is_active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
              Inactive
            </span>
          )}
        </div>

        <div className="flex items-center space-x-1">
          {editing ? (
            <>
              <button onClick={handleSave} disabled={updating} className="text-green-600 hover:text-green-700 p-1" title="Save">
                <Check className="h-4 w-4" />
              </button>
              <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600 p-1" title="Cancel">
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              {!ay.is_locked && (
                <>
                  <button
                    onClick={() => setShowBackfill(true)}
                    className="text-gray-400 hover:text-blue-600 p-1"
                    title="Assign unscoped records to this year"
                  >
                    <DatabaseZap className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-blue-600 p-1" title="Edit name">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setShowLockReadiness(true)} className="text-gray-400 hover:text-amber-600 p-1" title="Lock (make read-only)">
                    <Lock className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {!ay.is_locked && !ay.is_active ? (
                confirmingActivate ? (
                  <>
                    <span className="text-xs text-green-600 font-medium mr-1">Activate?</span>
                    <button
                      onClick={handleActivate}
                      disabled={updating}
                      className="text-green-600 hover:text-green-700 p-1"
                      title="Confirm activate"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setConfirmingActivate(false)}
                      disabled={updating}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Cancel"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmingActivate(true)}
                    disabled={updating}
                    className="text-gray-400 hover:text-green-600 p-1"
                    title="Reactivate year"
                  >
                    <Power className="h-3.5 w-3.5" />
                  </button>
                )
              ) : ay.has_data ? (
                confirmingDeactivate ? (
                  <>
                    <span className="text-xs text-amber-600 font-medium mr-1">Deactivate?</span>
                    <button
                      onClick={handleDeactivate}
                      disabled={updating}
                      className="text-amber-600 hover:text-amber-700 p-1"
                      title="Confirm deactivate"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setConfirmingDeactivate(false)}
                      disabled={updating}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Cancel"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmingDeactivate(true)}
                    disabled={updating}
                    className="text-gray-400 hover:text-amber-600 p-1"
                    title="Deactivate year (has associated data — cannot delete)"
                  >
                    <Power className="h-3.5 w-3.5" />
                  </button>
                )
              ) : confirmingDelete ? (
                <>
                  <span className="text-xs text-red-600 font-medium mr-1">Delete?</span>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-red-600 hover:text-red-700 p-1"
                    title="Confirm delete"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    disabled={deleting}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    title="Cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  disabled={deleting}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AuditYearSettings() {
  const { data: years, isLoading, isError } = useAuditYears();
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Audit Years</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Each audit year is the working context for your team. Select a year in the sidebar
            to see that year's processes, issues, test runs, and reports.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-1.5 px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded-md hover:bg-blue-800 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>New Year</span>
        </button>
      </div>

      {showForm && <CreateAuditYearForm onClose={() => setShowForm(false)} />}

      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}
      {isError && <div className="text-sm text-red-600">Failed to load audit years.</div>}
      {years && years.length === 0 && !showForm && (
        <div className="text-sm text-gray-500 italic">
          No audit years yet. Click "New Year" to create your first one.
        </div>
      )}

      <div className="space-y-2">
        {years?.map((ay) => <AuditYearRow key={ay.id} ay={ay} />)}
      </div>
    </div>
  );
}
